import { clsx, type ClassValue } from "clsx";
import { type KeyboardEvent } from "react";
import { twMerge } from "tailwind-merge";
import * as CryptoJS from "crypto-js";
import { EMAIL_REGEX, ENABLE_DATA_SUBSTITUTION_TYPE } from "../constants";
import {
  OptionValueStringType,
  PermissionMap,
  PermissionState,
  TagsListType,
  UserInfo,
} from "../type/type";
import { SortingState } from "@tanstack/react-table";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// Function to convert strings to title case.
// This function takes a string as input, converts it to lowercase, splits it into words, capitalizes the first letter of each word, and then joins them back together with spaces.
export const toTitleCase = (value: string): string => {
  return value
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const getUniqueTags = (
  tags: { id: number; name: string }[],
): TagsListType[] => {
  const uniqueTags: TagsListType[] = [];
  const seenTags = new Set<string>();
  for (const tag of tags) {
    const normalizedName = tag.name.trim().toLowerCase();
    if (!seenTags.has(normalizedName)) {
      seenTags.add(normalizedName);
      uniqueTags.push({ ...tag, name: normalizedName });
    }
  }
  return uniqueTags;
};

export const buildTooltipList = (items: string[]): string => items.join(", ");

export const disableDataSubstitutionTypes = (
  option: OptionValueStringType,
): boolean => {
  return !ENABLE_DATA_SUBSTITUTION_TYPE.includes(option.value);
};

export const getInitials = (username: string): string => {
  if (!username) {
    return "";
  }

  const words = username
    .trim()
    .split(/\s+/) // handles multiple spaces
    .filter(Boolean);

  if (words.length === 0) {
    return "";
  }

  if (words.length === 1) {
    // If only one word → take first 2 letters
    return words[0].slice(0, 2).toUpperCase();
  }

  // Take first letter of first & last word
  const first = words[0][0];
  const last = words[words.length - 1][0];

  return (first + last).toUpperCase();
};

export const safeString = (value: unknown): string =>
  typeof value === "string" ? value : "";

export const safeNumber = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

export const getCSSVariable = (variable: string, fallback: string): string => {
  if (typeof window === "undefined") {
    return fallback;
  }
  return (
    getComputedStyle(document.documentElement)
      .getPropertyValue(variable)
      .trim() || fallback
  );
};

export const handleNumericKeyDown = (
  e: KeyboardEvent<HTMLInputElement>,
): void => {
  if (e.key === "e" || e.key === "E" || e.key === "+" || e.key === "-") {
    e.preventDefault();
  }
};

/**
 * Validates Australian phone numbers
 * Supports formats: +61, (02), 04, with spaces or hyphens
 * @param phoneNumber - Phone number string to validate
 * @returns true if valid Australian phone number
 */
export const validatePhoneNumber = (phoneNumber: string): boolean => {
  if (!phoneNumber) {
    return true;
  } // Empty is valid (optional field)

  // Remove spaces, hyphens, and parentheses for validation
  const cleaned = phoneNumber.replace(/[\s\-()]/g, "");

  // Australian phone number patterns:
  // Landline: (02) 1234 5678 or 02 1234 5678 (10 digits with area code)
  // Mobile: 04XX XXX XXX (10 digits starting with 04)
  // International: +61 X XXXX XXXX (11-13 digits with +61)

  // Pattern 1: +61 followed by 9 digits (mobile without leading 0) or 10 digits (landline with area code)
  if (cleaned.startsWith("+61")) {
    const withoutPrefix = cleaned.substring(3);
    return /^[2-9]\d{8,9}$/.test(withoutPrefix);
  }

  // Pattern 2: 10 digits starting with 0 (Australian format)
  if (cleaned.startsWith("0")) {
    return /^0[2-9]\d{8}$/.test(cleaned);
  }

  // Pattern 3: Alternative format without leading 0 (9 digits)
  if (/^[2-9]\d{8}$/.test(cleaned)) {
    return true;
  }

  return false;
};

/**
 * Rounds a number to a specific number of decimal places and formats it as a string
 * @param value - Number or string to round
 * @param decimals - Number of decimal places
 * @returns Formatted string
 */
export const getRoundOffValue = (
  value: number | string | null | undefined,
  decimals: number,
): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  const num = typeof value === "string" ? parseFloat(value) : (value as number);

  if (isNaN(num)) {
    return "N/A";
  }

  return num.toFixed(decimals);
};

/**
 * Generic function to sort data with support for string, numeric, and boolean fields.
 * Uses numeric-aware locale comparison for string fields.
 * Handles null/undefined values gracefully.
 * @param data - Array of data to sort
 * @param sortingState - Sorting state from @tanstack/react-table
 * @returns Sorted array
 */
export const sortData = <T extends Record<string, unknown>>(
  data: T[],
  sortingState: SortingState,
): T[] => {
  if (!sortingState || sortingState.length === 0) {
    return data;
  }

  const sorted = [...data];
  const { id, desc } = sortingState[0];

  sorted.sort((a, b) => {
    const aValue = a[id as keyof T];
    const bValue = b[id as keyof T];

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) {
      if (bValue === null || bValue === undefined) {
        return 0;
      }
      return desc ? -1 : 1;
    }
    if (bValue === null || bValue === undefined) {
      return desc ? 1 : -1;
    }

    // Handle string comparison with numeric-aware sorting
    if (typeof aValue === "string" && typeof bValue === "string") {
      const comparison = aValue.localeCompare(bValue, undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return desc ? -comparison : comparison;
    }

    // Handle numeric/boolean comparison
    if (aValue < bValue) {
      return desc ? 1 : -1;
    }
    if (aValue > bValue) {
      return desc ? -1 : 1;
    }
    return 0;
  });

  return sorted;
};

/**
 * Sanitizes a timestamp string for use in filenames.
 * Replaces colons and periods with hyphens to ensure cross-platform compatibility.
 * ISO strings contain characters like colons that are invalid in many file systems.
 * @param isoString - ISO timestamp string (e.g., from new Date().toISOString())
 * @returns Sanitized filename-safe timestamp
 */
export const sanitizeTimestampForFilename = (isoString: string): string => {
  return isoString.replace(/[:.]/g, "-");
};

export const extractPermissionStateFromUserInfo = (
  userInfo: UserInfo,
): PermissionState => {
  const permissions: PermissionMap = {};

  try {
    if (userInfo.permissions) {
      const rawPermissions = JSON.parse(userInfo.permissions);

      if (Array.isArray(rawPermissions)) {
        rawPermissions.forEach((item) => {
          if (
            Array.isArray(item) &&
            item.length === 2 &&
            typeof item[0] === "number" &&
            typeof item[1] === "number"
          ) {
            const [moduleId, permissionMask] = item;
            permissions[moduleId] = permissionMask;
          }
        });
      }
    }
  } catch {
    // Fall back to empty permissions if parsing fails
  }

  const isSuperAdmin = userInfo.is_super_admin ?? false;
  const isCompanyAdmin = userInfo.is_company_admin ?? false;
  const userId = userInfo.id ?? null;
  const companyId = userInfo.company_id ?? null;
  const email = userInfo.email ?? null;
  const isTrialUser = userInfo.is_trial_user ?? false;
  const userExpiry = userInfo.user_expiry ?? null;

  return {
    permissions,
    isSuperAdmin,
    isCompanyAdmin,
    userId,
    companyId,
    email,
    isTrialUser,
    userExpiry,
    isLoaded: true,
  };
};

export const encryptMeterId = (meterId: string): string => {
  const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPT_DECRYPT_KEY;
  const ENCRYPTION_IV = import.meta.env.VITE_ENCRYPT_DECRYPT_IV_KEY;

  if (ENCRYPTION_KEY && ENCRYPTION_IV) {
    const key = CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY);
    const iv = CryptoJS.enc.Utf8.parse(ENCRYPTION_IV);

    const encrypted = CryptoJS.AES.encrypt(meterId, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    const abc = encrypted
      .toString()
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
    const encoded = encodeURIComponent(abc);
    return encoded;
  } else {
    return meterId;
  }
};

export const isSupersetTokenExpired = (token: string): boolean => {
  try {
    const tokenParts = token.split(".");
    if (tokenParts.length !== 3) {
      return true;
    }

    // Decode base64url safely (replace - with +, _ with /, and add padding)
    const normalizedValue = tokenParts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padding = (4 - (normalizedValue.length % 4)) % 4;
    const decodedPayload = atob(
      normalizedValue.padEnd(normalizedValue.length + padding, "="),
    );

    const payload = JSON.parse(decodedPayload) as { exp?: unknown };

    // Validate exp exists and is a valid number
    if (typeof payload.exp !== "number" || !Number.isFinite(payload.exp)) {
      return true; // Treat invalid exp as expired
    }

    return payload.exp * 1000 < Date.now();
  } catch {
    return true; // Treat decode/parse errors as expired
  }
};

export const noopPropType = (): null => null;

export const NOOP_HANDLER = (): void => {
  return;
};

export const toStringValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }

  return "";
};

export const toNumberValue = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  return 0;
};

export const isValidEmail = (email: string): boolean => {
  const trimmedEmail = email.trim();
  return EMAIL_REGEX.test(trimmedEmail);
};
